import React, { useCallback, useEffect, useState } from 'react';
import { getMemberAccessToken, supabase } from '../auth/supabase';
import {
  getNativeNotificationStatus,
  nativeNotificationsAvailable,
  openNativeNotificationSettings,
  registerNativeNotifications,
  requestNativeNotificationPermission,
} from '../native/nativeNotifications';

const SMS_PRIVACY_URL = 'https://sacmetrolinemedic.app/privacy';
const SMS_TERMS_URL = 'https://sacmetrolinemedic.app/sms-terms';

function isLineMedic(profile) {
  return String(profile?.member_kind || 'line_medic') === 'line_medic';
}

async function authorizedHeaders() {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function getServerNotificationStatus() {
  const response = await fetch('/api/notifications', { cache: 'no-store', headers: await authorizedHeaders() });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to load notification preferences.');
  return payload;
}

function carrierOptOutDetails(error) {
  const message = String(error?.message || '');
  const marker = 'SMS_CARRIER_OPTED_OUT|';
  const markerIndex = message.indexOf(marker);
  if (markerIndex < 0) return null;
  return {
    restartNumber: message.slice(markerIndex + marker.length).split(/\s/)[0].trim(),
  };
}

async function registerWithBackend(registration) {
  if (!registration?.token) return;
  const response = await fetch('/api/notifications', {
    method: 'POST',
    headers: await authorizedHeaders(),
    body: JSON.stringify({
      action: 'register',
      token: registration.token,
      permission: registration.permission,
      environment: registration.environment,
      bundleId: registration.bundleId,
      appVersion: registration.appVersion,
      appBuild: registration.appBuild,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to register notifications.');
}

async function sendSmsOptInConfirmation() {
  const response = await fetch('/api/notifications', {
    method: 'POST',
    headers: await authorizedHeaders(),
    body: JSON.stringify({ action: 'sms_opt_in_confirmation' }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Unable to send the Operational SMS enrollment confirmation.');
    error.code = payload?.code || '';
    error.restartNumber = payload?.restartNumber || '';
    throw error;
  }
  return payload;
}

function formatSmsNumber(value) {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw || 'the Line Medic number';
}

function permissionLabel(permission) {
  if (permission === 'enabled') return 'Enabled';
  if (permission === 'provisional') return 'Provisional';
  if (permission === 'denied') return 'Off in iOS Settings';
  return 'Not configured';
}

export default function NotificationPreferences({ profile, compact = false, onProfileChanged }) {
  const native = nativeNotificationsAvailable();
  const lineMedic = isLineMedic(profile);
  const [nativeStatus, setNativeStatus] = useState({ permission: 'unknown', registered: false });
  const [serverStatus, setServerStatus] = useState({
    operationalSmsOptIn: Boolean(profile?.operational_sms_opt_in),
    mobileVerified: Boolean(profile?.mobile_verified_at),
    carrierSmsOptedOut: false,
    smsRestartNumber: '',
    devices: [],
  });
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [smsRecovery, setSmsRecovery] = useState(null);

  const load = useCallback(async () => {
    if (!lineMedic) return;
    setError('');
    try {
      const requests = [];
      if (native) requests.push(getNativeNotificationStatus());
      else requests.push(Promise.resolve({ permission: 'unknown', registered: false }));
      requests.push(getServerNotificationStatus());
      const [device, server] = await Promise.all(requests);
      setNativeStatus(device || { permission: 'unknown', registered: false });
      setServerStatus(server || {});
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load notification preferences.');
    }
  }, [native, lineMedic]);

  useEffect(() => { load(); }, [load]);

  if (!lineMedic) return null;

  const enablePush = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const permission = await requestNativeNotificationPermission();
      setNativeStatus(permission || {});
      if (permission?.permission === 'denied') {
        setMessage('Notifications are off in iOS Settings. You can turn them on there at any time.');
        return;
      }
      if (!['enabled', 'provisional'].includes(permission?.permission)) {
        setMessage('Notification permission was not enabled.');
        return;
      }
      const registration = await registerNativeNotifications();
      await registerWithBackend(registration);
      setNativeStatus(registration || permission);
      setMessage('Line Medic notifications are enabled on this iPhone.');
      await load();
    } catch (enableError) {
      setError(enableError?.message || 'Unable to enable notifications.');
    } finally {
      setBusy(false);
    }
  };

  const toggleSms = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    setSmsRecovery(null);
    const enabled = !Boolean(serverStatus?.operationalSmsOptIn);
    if (enabled && !smsConsentChecked) {
      setError('Confirm the Operational SMS consent checkbox before enabling recurring text alerts.');
      setBusy(false);
      return;
    }
    try {
      if (enabled) {
        const currentServerStatus = await getServerNotificationStatus();
        setServerStatus(currentServerStatus || {});
        if (currentServerStatus?.carrierSmsOptedOut) {
          setSmsRecovery({ restartNumber: currentServerStatus?.smsRestartNumber || '' });
          setBusy(false);
          return;
        }
      }

      const { data, error: rpcError } = await supabase.rpc('set_operational_sms_preference', { enabled });
      if (rpcError) {
        const carrierBlock = carrierOptOutDetails(rpcError);
        if (carrierBlock) {
          setServerStatus(current => ({
            ...current,
            operationalSmsOptIn: false,
            carrierSmsOptedOut: true,
            smsRestartNumber: carrierBlock.restartNumber || current?.smsRestartNumber || '',
          }));
          setSmsRecovery({ restartNumber: carrierBlock.restartNumber || serverStatus?.smsRestartNumber || '' });
          setBusy(false);
          return;
        }
        throw rpcError;
      }
      const row = Array.isArray(data) ? data[0] : data;
      const preferenceEnabled = Boolean(row?.operational_sms_opt_in ?? enabled);

      if (enabled && preferenceEnabled) {
        try {
          await sendSmsOptInConfirmation();
        } catch (confirmationError) {
          const { error: rollbackError } = await supabase.rpc('set_operational_sms_preference', { enabled: false });
          if (rollbackError) {
            setServerStatus(current => ({ ...current, operationalSmsOptIn: true }));
            throw new Error('Operational SMS is on, but the required enrollment confirmation could not be sent. Turn Operational SMS off and try again after messaging service is restored.');
          }
          setServerStatus(current => ({ ...current, operationalSmsOptIn: false }));
          if (confirmationError?.code === 'TWILIO_21610') {
            setSmsRecovery({
              restartNumber: confirmationError?.restartNumber || '',
            });
            throw confirmationError;
          }
          throw new Error(confirmationError?.message || 'Operational SMS was not enabled because the enrollment confirmation could not be sent.');
        }
      }

      setServerStatus(current => ({ ...current, operationalSmsOptIn: preferenceEnabled }));
      setSmsConsentChecked(false);
      setMessage(enabled
        ? 'Operational SMS is on. A confirmation text was sent to your verified mobile number.'
        : 'Operational SMS is off. Security and verification texts still work normally.');
      if (onProfileChanged) {
        const { data: refreshed, error: refreshError } = await supabase.rpc('refresh_member_approval');
        if (!refreshError && refreshed) onProfileChanged(refreshed);
      }
    } catch (smsError) {
      if (smsError?.code === 'TWILIO_21610') {
        setSmsRecovery(current => current || { restartNumber: smsError?.restartNumber || '' });
        setError('');
      } else {
        setError(smsError?.message || 'Unable to change operational SMS preference.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? '' : 'tracking-card'} style={compact ? undefined : { marginTop: 14 }}>
      <h3>Operational notifications</h3>
      <p>Program Feed and Prescribed Burn notifications are operational alerts. Account verification and security texts are separate and remain available regardless of these preferences.</p>

      {native ? (
        <div style={{ marginTop: 12 }}>
          <p>iPhone push notifications: <strong>{permissionLabel(nativeStatus?.permission)}</strong></p>
          {['unknown'].includes(nativeStatus?.permission) && (
            <button className="tracking-btn" type="button" disabled={busy} onClick={enablePush}>{busy ? 'Working…' : 'Enable Notifications'}</button>
          )}
          {nativeStatus?.permission === 'denied' && (
            <button className="tracking-btn" type="button" disabled={busy} onClick={openNativeNotificationSettings}>Open iOS Settings</button>
          )}
          {['enabled','provisional'].includes(nativeStatus?.permission) && <p style={{ opacity: .75 }}>This iPhone is eligible for Program Feed and Prescribed Burn push alerts.</p>}
        </div>
      ) : (
        <p style={{ opacity: .75 }}>Push notification controls are available in the Line Medic iPhone app.</p>
      )}

      <div style={{ marginTop: 14 }}>
        <p>Operational SMS: <strong>{serverStatus?.operationalSmsOptIn ? 'On' : 'Off'}</strong></p>
        {serverStatus?.mobileVerified ? (
          serverStatus?.operationalSmsOptIn ? (
            <button className="tracking-btn" type="button" disabled={busy} onClick={toggleSms}>
              {busy ? 'Working…' : 'Turn Off Operational SMS'}
            </button>
          ) : (
            <>
              {serverStatus?.carrierSmsOptedOut && (
                <p style={{ margin: '8px 0 12px', color: 'var(--danger-text)' }}>
                  Text delivery is paused. Text <strong>START</strong> to <strong>{formatSmsNumber(serverStatus?.smsRestartNumber)}</strong> before enabling Operational SMS.
                </p>
              )}
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '12px 0' }}>
                <input
                  type="checkbox"
                  checked={smsConsentChecked}
                  onChange={event => setSmsConsentChecked(event.target.checked)}
                  style={{ marginTop: 4, width: 18, height: 18, flex: '0 0 auto' }}
                />
                <span>
                  I agree to receive recurring operational text messages from <strong>Line Medic</strong> at my verified mobile number, including Program Feed updates, Prescribed Burn opportunities, roster or assignment changes, and related operational alerts. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out and <strong>HELP</strong> for help. Consent is optional and is not required to use Line Medic.
                </span>
              </label>
              <p style={{ marginTop: 8, opacity: .8 }}>
                <a href={SMS_PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
                {' · '}
                <a href={SMS_TERMS_URL} target="_blank" rel="noreferrer">SMS Terms</a>
              </p>
              <button className="tracking-btn" type="button" disabled={busy || !smsConsentChecked} onClick={toggleSms}>
                {busy ? 'Working…' : 'Enable Operational SMS'}
              </button>
            </>
          )
        ) : (
          <p style={{ opacity: .75 }}>Verify your mobile number before enabling operational SMS.</p>
        )}
      </div>

      {error && <p style={{ marginTop: 10, color: '#b42318' }}>{error}</p>}
      {message && <p style={{ marginTop: 10, opacity: .85 }}>{message}</p>}

      {smsRecovery && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sms-recovery-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 32, .62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              width: 'min(430px, 100%)',
              background: 'var(--card-bg)',
              color: 'var(--ink)',
              border: '1px solid var(--border-color)',
              borderRadius: 16,
              padding: 22,
              boxShadow: 'var(--shadow)',
            }}
          >
            <h3 id="sms-recovery-title" style={{ marginTop: 0, color: 'var(--ink-strong)' }}>Text messages are still paused</h3>
            <p style={{ marginBottom: 10 }}>
              Your number is still unsubscribed from Line Medic operational texts.
            </p>
            <p style={{ marginBottom: 10 }}>
              Text <strong>START</strong> to <strong>{formatSmsNumber(smsRecovery.restartNumber)}</strong>.
            </p>
            <p style={{ marginBottom: 18, opacity: .82 }}>
              Wait for the confirmation that you are resubscribed, then return here and enable Operational SMS again.
            </p>
            <button
              className="tracking-btn"
              type="button"
              onClick={() => setSmsRecovery(null)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
